// X-Ray › Workflows — mine recurring agent workflows from the traces in
// a scope and propose a reusable tool for each.
//
// This is the agentic pipeline we converged on after rejecting pure
// deterministic signature-mining: determinism can't see that two
// sessions did "the same workflow" via different tools, so the semantics
// are done by sub-agents and the only deterministic step left is L0
// extraction (turn a raw api_request body into a compact, readable
// timeline). Stages:
//
//   L0  extract     — deterministic, pure, unit-tested. Raw body →
//                     "tool timeline + conversational spine", results
//                     truncated, entity facets unioned for the ledger.
//   L1+L2 read      — one sub-agent per SESSION reads the timeline top
//                     to bottom and narrates the workflow (steps + intent).
//                     A session larger than the reader's context rolls
//                     across chunks: an append-only step log (the deltas)
//                     plus a bounded carried state (the story so far) plus
//                     the deterministic entity ledger (anti-drift).
//   L3  cluster     — LLM-as-judge, online running-representatives. No
//                     embeddings (must work in-browser). Each batch of
//                     session summaries is assigned to an existing
//                     workflow cluster or starts a new one.
//   L3' verify      — single-pass ground-truth check per cluster: drop
//                     members that don't actually share the workflow.
//   L4  suggest     — per surviving cluster, a pseudo-code sketch of a
//                     tool that would collapse the workflow into one call,
//                     plus a read-only / side-effecting classification.
//                     The sketch is a CANDIDATE for human review — it is
//                     never executed or installed.
//
// All sub-agents run in-browser via runSubAgent (same path as X-Ray
// Describe and the influence tracer); there is no backend dependency.
// The grouping is the SCOPE itself — the caller hands us the scan
// window; we don't re-discover clusters with skwach.

import { runSubAgent, runWithConcurrency } from "./subAgent.js";
import { hydratePrompt } from "./triggers/triggerHarness.js";
import { chunkSessionTurns, extractFacets, canonicalizeSessions, toSkillName } from "./workflowExtract.js";

// ── Tunables (thin-slice; deliberately conservative). The window-size
// control is the existing scan scrubber, so these bound per-session and
// per-stage cost rather than how many sessions we touch. (L0 extraction
// constants live in workflowExtract.js, the pure/unit-tested layer.)
const READER_CONCURRENCY = 4; // sessions read in parallel; each session's rolling chain stays sequential
const MAX_SESSIONS = 80; // hard cap on sessions read per run — this is the heaviest scan in the app (one reader per session); surfaced when it bites
const MAX_STEPS_PER_SESSION = 60; // assembly guard; within-trace phase-fold is a follow-on, we cap + flag
const JUDGE_BATCH = 12; // session summaries per clustering pass (running representatives)
const MAX_CLUSTERS = 24; // safety cap on the running rep list

// ===================================================================
// L1+L2 — per-session workflow read (rolling)
// ===================================================================

const READER_SCHEMA = {
  intent: "string — the session's overall goal, as understood SO FAR; one sentence",
  steps: [
    {
      summary: "string — what this step accomplished, read IN CONTEXT of what came before (not a literal restatement of the command)",
      tool: "string — the tool or program used, e.g. 'Bash: git', 'Edit', 'mcp:linear'",
    },
  ],
  openThreads: ["string — goals still unresolved at the end of this chunk; carried into the next"],
};

const READER_SYS =
  "You read a captured agent session's execution timeline and narrate the WORKFLOW it performed — the " +
  "actual task the organization was trying to accomplish. The timeline is in order: SYSTEM (harness setup), " +
  "USER asks, ASSISTANT says, CALL <tool>(args), RESULT. Tool payloads are truncated — infer intent, do not quote. " +
  "Use ALL of it — including SYSTEM-prompt memory, project context, and task framing — to UNDERSTAND the work. " +
  "But the harness's own machinery is NOT the workflow: the agent's persona or self-introduction, its capability " +
  "descriptions, tool/skill enumeration, <system-reminder> blocks, and tool-result banners are scaffolding — and " +
  "they appear in BOTH system and user turns. NEVER report machinery as a step. Summarize each REAL step in the " +
  "CONTEXT of what preceded it (a 'git commit' after a long debug arc is 'commits the fix', not 'runs git'); " +
  "collapse trivial bookkeeping (TodoWrite, repeated reads of one file) into the step it serves. If a session is " +
  "ONLY setup/machinery with no real task work, return no steps. Return ordered steps, the overall intent, and any " +
  "unresolved threads. Base everything ONLY on the timeline; never invent.";

function buildReaderTask({ chunkText, state, isFirst, isLast, chunkIndex, chunkCount, facets, stepCount }) {
  const lines = [];
  if (chunkCount > 1) {
    lines.push(`This session is long; you are reading chunk ${chunkIndex + 1} of ${chunkCount}.`);
    if (!isFirst) {
      lines.push(`STORY SO FAR: ${state.intent || "(none yet)"}`);
      if (state.openThreads?.length) lines.push(`OPEN THREADS: ${state.openThreads.join("; ")}`);
      lines.push(`Steps already recorded: ${stepCount}. Emit ONLY the NEW steps in this chunk — do not repeat earlier ones.`);
    }
  }
  const facetLine = facets
    ? Object.entries(facets)
        .filter(([, v]) => v.length)
        .map(([k, v]) => `${k}: ${v.join(", ")}`)
        .join(" | ")
    : "";
  if (facetLine) lines.push(`ENTITIES SEEN: ${facetLine}`);
  lines.push("", "TIMELINE:", chunkText, "");
  lines.push(
    isLast
      ? "This is the final chunk. Return the overall intent and the NEW steps; openThreads should be only the truly-unresolved ones."
      : "Return the NEW steps in this chunk, your updated intent, and openThreads to carry forward.",
  );
  return lines.join("\n");
}

async function summarizeSession({ session, turns, model, signal }) {
  const facets = extractFacets(turns);
  const chunks = chunkSessionTurns(turns);
  const state = { intent: "", openThreads: [] };
  const steps = [];
  for (let i = 0; i < chunks.length; i++) {
    if (signal?.aborted) break;
    if (steps.length >= MAX_STEPS_PER_SESSION) break;
    const r = await runSubAgent({
      role: "task",
      model,
      signal,
      outputSchema: READER_SCHEMA,
      systemPrompt: READER_SYS,
      task: buildReaderTask({
        chunkText: chunks[i],
        state,
        isFirst: i === 0,
        isLast: i === chunks.length - 1,
        chunkIndex: i,
        chunkCount: chunks.length,
        facets,
        stepCount: steps.length,
      }),
    });
    // A chunk that won't summarize shouldn't kill the session — keep what
    // we have and move on; the cluster step tolerates thin reads.
    if (r.status !== "done" || !r.result || typeof r.result !== "object") continue;
    const res = r.result;
    if (Array.isArray(res.steps)) {
      for (const s of res.steps) {
        if (steps.length >= MAX_STEPS_PER_SESSION) break;
        const summary = String(s?.summary || "").trim();
        const tool = String(s?.tool || "").trim();
        if (summary || tool) steps.push({ summary, tool });
      }
    }
    if (res.intent) state.intent = String(res.intent).trim();
    if (Array.isArray(res.openThreads)) state.openThreads = res.openThreads.map(String);
  }
  if (!steps.length && !state.intent) return null;
  return {
    sessionId: session.sessionId,
    promptId: session.promptId,
    label: session.label,
    intent: state.intent,
    steps,
    facets,
    truncated: steps.length >= MAX_STEPS_PER_SESSION,
  };
}

// Compact card for the judge / verifier / suggester — intent + the first
// several steps. Keeps the running-rep prompts bounded.
function summaryCard(s) {
  const steps = s.steps
    .slice(0, 8)
    .map((x, i) => `  ${i + 1}. ${x.summary}${x.tool ? ` [${x.tool}]` : ""}`)
    .join("\n");
  return `session ${s.sessionId}\nintent: ${s.intent || s.label || "(unknown)"}\nsteps:\n${steps || "  (none)"}`;
}

// ===================================================================
// L3 — LLM-as-judge clustering (online running-representatives)
// ===================================================================

const JUDGE_SCHEMA = {
  assignments: [
    {
      sessionId: "string",
      joinClusterId: "string — id of an existing cluster this session's workflow matches, or empty",
      newName: "string — if no existing cluster fits, a short name for a NEW workflow cluster (else empty)",
      newDescription: "string — one-sentence description of the new cluster (else empty)",
    },
  ],
};

const JUDGE_SYS =
  "You group agent sessions by the WORKFLOW they performed — the shape of the work, not the topic. You " +
  "maintain a running list of workflow clusters. For each session in the batch, EITHER assign it to the " +
  "existing cluster whose workflow it matches (set joinClusterId) OR, if none fits, start a new cluster " +
  "(set newName + newDescription). Two sessions share a cluster when the SAME reusable tool would serve " +
  "both, even if details differ. Prefer joining; create a new cluster only when the workflow is genuinely " +
  "distinct. Never force unrelated sessions together. Never form a cluster around harness machinery — agent " +
  "initialization, persona/capability description, tool/skill enumeration, or session setup are scaffolding, " +
  "not workflows the organization runs; do not cluster them. Return one assignment per session in the batch.";

export async function clusterWorkflows({ summaries, model, signal, onProgress }) {
  const clusters = [];
  const byId = new Map();
  const placed = new Set();
  let nextId = 1;
  let done = 0;
  let hitClusterCap = false;
  for (let i = 0; i < summaries.length; i += JUDGE_BATCH) {
    if (signal?.aborted) break;
    const batch = summaries.slice(i, i + JUDGE_BATCH);
    const repList = clusters.length
      ? clusters.map((c) => `- id=${c.id} "${c.name}": ${c.description} (${c.members.length} so far)`).join("\n")
      : "(none yet — start the first clusters)";
    const r = await runSubAgent({
      role: "task",
      model,
      signal,
      outputSchema: JUDGE_SCHEMA,
      systemPrompt: JUDGE_SYS,
      task: `EXISTING CLUSTERS:\n${repList}\n\nSESSIONS TO ASSIGN:\n${batch.map(summaryCard).join("\n\n")}`,
    });
    const assignments = r.status === "done" && r.result && Array.isArray(r.result.assignments) ? r.result.assignments : [];
    const bySession = new Map(batch.map((s) => [s.sessionId, s]));
    for (const a of assignments) {
      const s = bySession.get(String(a?.sessionId));
      if (!s || placed.has(s.sessionId)) continue;
      let cluster = a.joinClusterId ? byId.get(String(a.joinClusterId)) : null;
      if (!cluster) {
        if (clusters.length >= MAX_CLUSTERS) {
          // Rep list full — leave the session UNCLUSTERED rather than
          // folding it into an unrelated cluster (which would manufacture
          // a junk group). Counted and surfaced below; a real version
          // would run a merge pass to reclaim the cap.
          hitClusterCap = true;
          continue;
        }
        cluster = {
          id: `c${nextId++}`,
          name: (a.newName || "Workflow").trim(),
          description: (a.newDescription || "").trim(),
          members: [],
        };
        clusters.push(cluster);
        byId.set(cluster.id, cluster);
      }
      cluster.members.push(s);
      placed.add(s.sessionId);
    }
    done += batch.length;
    onProgress?.({ phase: "cluster", done, total: summaries.length });
  }
  // Sessions the judge omitted or that overflowed the cluster cap are
  // left unplaced — report the count rather than dropping it silently.
  const unclustered = summaries.filter((s) => !placed.has(s.sessionId)).length;
  if (hitClusterCap) {
    // eslint-disable-next-line no-console
    console.warn(`[workflows] cluster cap (${MAX_CLUSTERS}) reached; ${unclustered} session(s) left unclustered`);
  }
  return { clusters: clusters.filter((c) => c.members.length), unclustered };
}

// ===================================================================
// L3' — verify a cluster against its members (single-pass)
// ===================================================================

const VERIFY_SCHEMA = {
  name: "string — refined cluster name",
  description: "string — refined one-sentence description of the shared workflow",
  drop: ["string — sessionIds that do NOT actually share this workflow"],
  coherent: "boolean — true if the kept members really are one workflow",
};

const VERIFY_SYS =
  "You audit a proposed workflow cluster. You get its name and its member session summaries. Drop any " +
  "session that does NOT genuinely share the workflow — be strict; a loose topical match is not enough. " +
  "Then give a refined name and one-sentence description for what the KEPT members actually have in common. " +
  "If after dropping there is no real shared workflow, set coherent=false. Also set coherent=false if the " +
  "cluster is just harness machinery — session setup, agent initialization, persona/capability description, " +
  "or tool enumeration — rather than real work toward a user goal.";

export async function verifyCluster({ cluster, model, signal }) {
  const r = await runSubAgent({
    role: "task",
    model,
    signal,
    outputSchema: VERIFY_SCHEMA,
    systemPrompt: VERIFY_SYS,
    task: `CLUSTER: "${cluster.name}" — ${cluster.description}\n\nMEMBERS:\n${cluster.members.map(summaryCard).join("\n\n")}`,
  });
  if (r.status !== "done" || !r.result) return cluster; // verify failed → keep as-is
  const drop = new Set((r.result.drop || []).map(String));
  const kept = cluster.members.filter((m) => !drop.has(m.sessionId));
  if (!kept.length || r.result.coherent === false) return null;
  return {
    ...cluster,
    name: (r.result.name || cluster.name).trim(),
    description: (r.result.description || cluster.description).trim(),
    members: kept,
  };
}

// ===================================================================
// L4 — suggest a reusable tool (pseudo-code, never executed)
// ===================================================================

const SUGGEST_SCHEMA = {
  pseudocode: "string — a concise pseudo-code sketch of a reusable tool that performs this workflow; parameterize the parts that varied across sessions",
  paramSurface: ["string — the inputs the tool would take (the parts that differed run-to-run)"],
  classification: "string — 'read-only' if the workflow only reads/queries/inspects, or 'side-effecting' if it writes/edits/commits/deploys/changes state",
  representativeSteps: ["string — the canonical ordered steps of this workflow"],
};

const SUGGEST_SYS =
  "You propose a reusable tool that would collapse a recurring agent workflow into one call. You get the " +
  "workflow's name, description, and member session summaries. Produce: a short pseudo-code sketch of the " +
  "tool (it does NOT need to be runnable — it is a candidate for human review), the parameters it would take " +
  "(the parts that varied across sessions), a read-only vs side-effecting classification, and the canonical " +
  "ordered steps. Keep the pseudo-code tight and concrete.";

export async function suggestTool({ cluster, model, signal }) {
  const r = await runSubAgent({
    role: "task",
    model,
    signal,
    outputSchema: SUGGEST_SCHEMA,
    systemPrompt: SUGGEST_SYS,
    task: `WORKFLOW: "${cluster.name}" — ${cluster.description}\n\nMEMBERS:\n${cluster.members.map(summaryCard).join("\n\n")}`,
  });
  const res = r.status === "done" && r.result ? r.result : {};
  const classification =
    res.classification === "side-effecting" ? "side-effecting" : res.classification === "read-only" ? "read-only" : "unknown";
  return {
    pseudocode: String(res.pseudocode || "").trim(),
    paramSurface: Array.isArray(res.paramSurface) ? res.paramSurface.map(String) : [],
    classification,
    representativeSteps: Array.isArray(res.representativeSteps) ? res.representativeSteps.map(String) : [],
  };
}

// ===================================================================
// L4 (alt) — suggest a reusable SKILL (the default artifact)
// ===================================================================
//
// A skill is the more faithful crystallization for most agent workflows:
// a procedure-with-judgment, not a single deterministic call. Shaped to
// the repo's .skill format (shared-skill-format) — frontmatter `name`
// (kebab) + `description` (the trigger, <=1024 chars) + a markdown body.
// Like the tool sketch, it is a CANDIDATE for human review — never
// installed or run.

const SKILL_SCHEMA = {
  skillName: "string — kebab-case skill name (lowercase words joined by hyphens), the SKILL.md frontmatter name",
  description: "string — the SKILL.md 'description' frontmatter: WHEN an agent should reach for this skill (the trigger it matches against), under ~1024 chars",
  instructions: ["string — ordered steps for the skill body, the playbook the agent follows"],
  inputs: ["string — inputs / preconditions the skill needs (the parts that varied across sessions)"],
  usesTools: ["string — the tools or commands this skill relies on"],
  classification: "string — 'read-only' if the workflow only reads/queries/inspects, or 'side-effecting' if it writes/edits/commits/deploys/changes state",
};

const SKILL_SYS =
  "You are drafting a reusable Claude Code skill (a SKILL.md playbook) that captures a recurring agent workflow so an " +
  "agent can follow it on demand. You get the workflow's name, description, and member session summaries. Produce: a " +
  "kebab-case skill name; a 'description' stating WHEN an agent should use this skill (the trigger it matches against), " +
  "under ~1024 chars; ordered instructions for the skill body; the inputs/preconditions it needs; the tools/commands it " +
  "relies on; and a read-only vs side-effecting classification. The draft is a CANDIDATE for human review — never installed or run.";

export async function suggestSkill({ cluster, model, signal }) {
  const r = await runSubAgent({
    role: "task",
    model,
    signal,
    outputSchema: SKILL_SCHEMA,
    systemPrompt: SKILL_SYS,
    task: `WORKFLOW: "${cluster.name}" — ${cluster.description}\n\nMEMBERS:\n${cluster.members.map(summaryCard).join("\n\n")}`,
  });
  const res = r.status === "done" && r.result ? r.result : {};
  const classification =
    res.classification === "side-effecting" ? "side-effecting" : res.classification === "read-only" ? "read-only" : "unknown";
  return {
    // Conform to the .skill frontmatter contract so the preview is valid:
    // kebab `name`, `description` clamped to the validator's 1024-char cap.
    skillName: toSkillName(res.skillName || cluster.name),
    description: String(res.description || "").trim().slice(0, 1024),
    instructions: Array.isArray(res.instructions) ? res.instructions.map(String) : [],
    inputs: Array.isArray(res.inputs) ? res.inputs.map(String) : [],
    usesTools: Array.isArray(res.usesTools) ? res.usesTools.map(String) : [],
    classification,
  };
}

// ===================================================================
// Orchestrator
// ===================================================================

export async function mineWorkflows({ windowTraces, model = null, signal, onProgress } = {}) {
  const allSessions = canonicalizeSessions(windowTraces);
  const sessionCount = allSessions.length;
  if (!sessionCount) {
    return { workflows: [], sessionCount: 0, scannedCount: 0, summarizedCount: 0, droppedForCap: 0, unclustered: 0 };
  }

  // Bound the fan-out: this is the heaviest scan in the app (a reader
  // sub-agent per session), so even though the user armed it explicitly
  // we cap the sessions read and surface what we skipped (no silent caps).
  const sessions = allSessions.slice(0, MAX_SESSIONS);
  const droppedForCap = sessionCount - sessions.length;
  if (droppedForCap > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[workflows] session cap (${MAX_SESSIONS}) reached; ${droppedForCap} of ${sessionCount} session(s) not scanned`);
  }
  const total = sessions.length;

  // L0 + L1 + L2 — hydrate, extract, read. Parallel across sessions
  // (each session's rolling chain stays sequential inside summarizeSession).
  onProgress?.({ phase: "summarize", done: 0, total });
  let sdone = 0;
  const tasks = sessions.map((session) => async () => {
    if (signal?.aborted) return null;
    let turns = null;
    try {
      const ev = await hydratePrompt(session.promptId);
      if (ev?.parsedConversation) turns = ev.parsedConversation.turns;
    } catch {
      /* unhydratable session — skip */
    }
    const summary = turns ? await summarizeSession({ session, turns, model, signal }) : null;
    sdone += 1;
    onProgress?.({ phase: "summarize", done: sdone, total });
    return summary;
  });
  const settled = await runWithConcurrency(tasks, READER_CONCURRENCY);
  const summaries = settled.filter((x) => x.ok && x.value).map((x) => x.value);
  if (signal?.aborted || !summaries.length) {
    return { workflows: [], sessionCount, scannedCount: total, summarizedCount: summaries.length, droppedForCap, unclustered: 0 };
  }

  // L3 — cluster
  const { clusters, unclustered } = await clusterWorkflows({ summaries, model, signal, onProgress });

  // L3' — verify each cluster against its members
  onProgress?.({ phase: "verify", done: 0, total: clusters.length });
  const verified = [];
  let vdone = 0;
  for (const c of clusters) {
    if (signal?.aborted) break;
    const v = await verifyCluster({ cluster: c, model, signal });
    if (v) verified.push(v);
    onProgress?.({ phase: "verify", done: ++vdone, total: clusters.length });
  }

  // L4 — suggest BOTH a skill (default artifact) and a tool per surviving
  // cluster, so the UI toggle is instant (no re-run). L4 is per-cluster
  // and cheap relative to the per-session reads, so doubling it is fine.
  onProgress?.({ phase: "suggest", done: 0, total: verified.length });
  const workflows = [];
  let wdone = 0;
  for (const c of verified) {
    if (signal?.aborted) break;
    const [skill, tool] = await Promise.all([
      suggestSkill({ cluster: c, model, signal }),
      suggestTool({ cluster: c, model, signal }),
    ]);
    workflows.push({
      id: c.id,
      name: c.name,
      description: c.description,
      // Classification is a property of the workflow, not the artifact;
      // prefer the skill's read (default), fall back to the tool's.
      classification: skill.classification !== "unknown" ? skill.classification : tool.classification,
      coverage: c.members.length,
      members: c.members.map((m) => ({ sessionId: m.sessionId, promptId: m.promptId, label: m.label || m.intent })),
      skill: {
        name: skill.skillName,
        whenToUse: skill.description,
        instructions: skill.instructions,
        inputs: skill.inputs,
        usesTools: skill.usesTools,
      },
      tool: {
        pseudocode: tool.pseudocode,
        paramSurface: tool.paramSurface,
        representativeSteps: tool.representativeSteps.length
          ? tool.representativeSteps
          : (c.members[0]?.steps || []).slice(0, 8).map((s) => s.summary),
      },
    });
    onProgress?.({ phase: "suggest", done: ++wdone, total: verified.length });
  }
  workflows.sort((a, b) => b.coverage - a.coverage);
  return { workflows, sessionCount, scannedCount: total, summarizedCount: summaries.length, droppedForCap, unclustered };
}
